import { createContext, useContext } from 'react';

/**
 * Surfaces whose polarity differs from the archetype around them. `glass-deep`
 * is the deep statement tone as translucent glass inside a light archetype: a
 * map overlay card, a deep inspector, a report hero rail.
 */
export const FM_SURFACES = ['glass-deep'] as const;
export type FmSurfaceKind = (typeof FM_SURFACES)[number];

/** Inner padding of a surface; `none` lets a full-bleed child own its edges. */
export const FM_SURFACE_PADDINGS = ['none', 'compact', 'comfortable'] as const;
export type FmSurfacePadding = (typeof FM_SURFACE_PADDINGS)[number];

/**
 * The ground a selector is drawn for. `on-dark` is its look on a deep band or a
 * glass-deep surface; inside FmSurface glass-deep it is chosen automatically.
 */
export const FM_SELECTOR_TONES = ['default', 'on-dark'] as const;
export type FmSelectorTone = (typeof FM_SELECTOR_TONES)[number];

/** The surface a control sits on, so a control picks its on-dark variant itself. */
export const FmSurfaceContext = createContext<FmSurfaceKind | null>(null);

export function useFmSurface(): FmSurfaceKind | null {
  return useContext(FmSurfaceContext);
}

/** Refuses a surface or padding the design system does not define. */
export function validateSurface(surface: string, padding: string): void {
  if (!(FM_SURFACES as readonly string[]).includes(surface)) {
    throw new Error(
      `FmSurface: surface "${surface}" is not one of ${FM_SURFACES.join(', ')}. ` +
        'A new surface is a design-system change with its own gallery specimen.',
    );
  }
  if (!(FM_SURFACE_PADDINGS as readonly string[]).includes(padding)) {
    throw new Error(
      `FmSurface: padding "${padding}" is not one of ${FM_SURFACE_PADDINGS.join(', ')}.`,
    );
  }
}
